import { Copy, Loader2, TrendingUp, Wallet as WalletIcon } from 'lucide-react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import { useWalletBalance } from '../../hooks/useWallet'
import { walletService } from '../../services/walletService'
import type { Wallet } from '../../types/wallet.types'

interface WalletBalanceProps {
  wallet: Wallet
}

export function WalletBalance({ wallet }: WalletBalanceProps) {
  const { data: balanceData, isLoading } = useWalletBalance(wallet.id)

  if (isLoading) {
    return (
      <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-6'>
        {[1, 2].map(i => (
          <Card key={i} className='border-0 shadow-md'>
            <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-3'>
              <CardTitle className='text-sm font-medium text-muted-foreground'>
                Loading...
              </CardTitle>
              <div className='h-8 w-8 rounded-lg bg-muted flex items-center justify-center'>
                <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
              </div>
            </CardHeader>
            <CardContent>
              <div className='flex items-center space-x-2'>
                <Loader2 className='h-4 w-4 animate-spin' />
                <span className='text-muted-foreground'>Loading</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  // Use real-time balance data if available, otherwise fall back to wallet data
  const cashBalance = balanceData
    ? walletService.formatAmount(balanceData.balance, wallet.currency)
    : walletService.formatAmount(wallet.balance, wallet.currency)

  const availableBalance = balanceData
    ? balanceData.credits_balance.toLocaleString() + ' credits'
    : wallet.credits_balance.toLocaleString() + ' credits'

  const ongoingBalance = balanceData
    ? walletService.formatAmount(
        balanceData.ongoing_balance * wallet.conversion_rate,
        wallet.currency
      )
    : walletService.formatAmount(
        wallet.ongoing_balance * wallet.conversion_rate,
        wallet.currency
      )

  const ongoingCredits = balanceData
    ? balanceData.ongoing_balance.toLocaleString() + ' credits'
    : wallet.ongoing_balance.toLocaleString() + ' credits'

  return (
    <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-6'>
      <Card>
        <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-3'>
          <CardTitle className='text-md font-medium text-muted-foreground'>
            Current Balance
          </CardTitle>
          <WalletIcon className='h-5 w-5 text-teal-600 dark:text-teal-400' />
        </CardHeader>
        <CardContent className='space-y-1'>
          <div className='text-2xl font-bold text-foreground'>
            {cashBalance}
          </div>
          <p className='text-sm text-muted-foreground flex items-center gap-1'>
            <Copy className='h-3 w-3' />
            {availableBalance}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-3'>
          <CardTitle className='text-md font-medium text-muted-foreground'>
            Ongoing Balance
          </CardTitle>
          <TrendingUp className='h-5 w-5 text-teal-600 dark:text-teal-400' />
        </CardHeader>
        <CardContent className='space-y-1'>
          <div className='text-2xl font-bold text-foreground'>
            {ongoingBalance}
          </div>
          <p className='text-sm text-muted-foreground flex items-center gap-1'>
            <Copy className='h-3 w-3' />
            {ongoingCredits}
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
